/**
 * Admin page for managing org-scoped templates.
 *
 * Displays a list of organisation templates with create, edit, and
 * deactivate actions. Admin role required. Deactivate loading state is
 * scoped per row to avoid all buttons showing spinners simultaneously.
 * Uses brand typography scale, .input/.label classes, and colour tokens.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus, FileText } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  useOrgTemplates,
  useCreateOrgTemplate,
  useDeactivateOrgTemplate,
} from "../../hooks/useOrgTemplates";
import { Card, Button, LoadingSpinner } from "../../components/common";
import Modal from "../../components/common/Modal";
import type { CreateOrgTemplateInput } from "../../types/orgTemplate";

export default function TemplateManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateOrgTemplateInput>({
    name: "",
    description: "",
    category: "",
  });

  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const { data, isLoading } = useOrgTemplates();
  const createTemplate = useCreateOrgTemplate();
  const deactivateTemplate = useDeactivateOrgTemplate();

  if (user?.role !== "admin") {
    return (
      <div className="space-y-6">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          Template Manager
        </h1>
        <Card>
          <p className="text-red-600">Access Denied. Admin role required.</p>
        </Card>
      </div>
    );
  }

  const templates = data?.items || [];

  const handleCreate = async () => {
    await createTemplate.mutateAsync(createForm);
    setIsCreateOpen(false);
    setCreateForm({ name: "", description: "", category: "" });
  };

  const handleDeactivate = async (id: string) => {
    setDeactivatingId(id);
    try {
      await deactivateTemplate.mutateAsync(id);
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          Template Manager
        </h1>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && templates.length === 0 && (
        <Card>
          <p className="text-slate-500 dark:text-slate-400 text-center py-4">
            No templates found. Create your first template to get started.
          </p>
        </Card>
      )}

      {!isLoading && templates.length > 0 && (
        <div className="overflow-hidden bg-white dark:bg-slate-800 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 rounded-lg">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400"
                >
                  Category
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400"
                >
                  Version
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-overline uppercase text-slate-500 dark:text-slate-400"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {templates.map((template) => (
                <tr
                  key={template.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-slate-400 mr-3" />
                      <div>
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {template.name}
                        </div>
                        {template.description && (
                          <div className="text-body-sm text-slate-500 dark:text-slate-400">
                            {template.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400 capitalize">
                    {template.category}
                  </td>
                  <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                    {template.currentVersion > 0
                      ? `v${template.currentVersion}`
                      : "No uploads"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-medium ${
                        template.isActive
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                          : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                      }`}
                    >
                      {template.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(`/generate/templates/${template.id}`)
                        }
                      >
                        Manage
                      </Button>
                      {template.isActive && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeactivate(template.id)}
                          loading={deactivatingId === template.id}
                          disabled={
                            deactivatingId !== null &&
                            deactivatingId !== template.id
                          }
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Template Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create Template"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="template-name" className="label">
              Name
            </label>
            <input
              id="template-name"
              type="text"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm({ ...createForm, name: e.target.value })
              }
              className="input mt-1"
              placeholder="e.g. QITC Comprehensive Template"
            />
          </div>
          <div>
            <label htmlFor="template-description" className="label">
              Description
            </label>
            <textarea
              id="template-description"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm({ ...createForm, description: e.target.value })
              }
              rows={3}
              className="input mt-1"
              placeholder="Brief description of this template"
            />
          </div>
          <div>
            <label htmlFor="template-category" className="label">
              Category
            </label>
            <input
              id="template-category"
              type="text"
              value={createForm.category}
              onChange={(e) =>
                setCreateForm({ ...createForm, category: e.target.value })
              }
              className="input mt-1"
              placeholder="e.g. government, commercial"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!createForm.name || !createForm.category}
              loading={createTemplate.isPending}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
